import { Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';

export const getBodyContentStyle = (theme: Theme): SystemStyleObject => ({
  '& .ProseMirror': {
    borderBottom: '1px solid transparent',
    '& h1, h2, h3, h4, h5, h6': {
      marginTop: '1rem',
      marginBottom: '0',
      fontWeight: 700,
    },
    '& h1': {
      fontSize: '1.857rem',
      lineHeight: '2.25rem',
    },
    '& h2': {
      fontSize: '1.5rem',
      lineHeight: '2rem',
    },
    '& h3': {
      fontSize: '1.25rem',
      lineHeight: '1.75rem',
    },
    '& h4': {
      fontSize: '1.125rem',
      lineHeight: '1.75rem',
    },
    '& h5': {
      fontSize: '1rem',
      lineHeight: '1.5rem',
    },
    '& h6': {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    },

    '& p.is-empty:first-of-type::before': {
      content: 'attr(data-placeholder)',
      color: theme.palette.grey[500],
      pointerEvents: 'none',
    },

    '&.ProseMirror-focused p.is-empty:first-of-type::before': {
      display: 'none',
    },

    // code input empty editor placeholder
    '& .react-renderer.node-codeBlock.is-empty': {
      position: 'relative',
      '&::before': {
        content: 'attr(data-placeholder)',
        color: theme.palette.grey[500],
        pointerEvents: 'none',
        position: 'absolute',
        top: 8,
        left: 8,
        right: 0,
        margin: 0,
        fontSize: 16,
        width: 'fit-content',
      },
    },

    // code input focused editor placeholder removal
    '&.ProseMirror-focused .react-renderer.node-codeBlock.is-empty::before': {
      display: 'none',
    },

    '& blockquote': {
      borderLeft: `3px solid ${
        theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]
      }`,
      color:
        theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[800],
      marginLeft: 0,
      marginRight: 0,
      paddingLeft: '10px',
    },

    '& table': {
      borderCollapse: 'collapse',
      tableLayout: 'fixed',
      margin: 0,
      // TODO: Maybe we want to give the users a way to toggle the width of the
      // table to be 100% or not? Similar to what Confluence does with their "Responsive" option
      minWidth: 75,
      width: '100%',
      overflowY: 'hidden',
      // If we don't have enough horizontal space for a table (when in read-only mode),
      // we need it to add a horizontal scrollbar, which requires using display:block
      // instead of display:table.
      overflowX: 'auto',
      display: 'table',

      '& td, th': {
        minWidth: '1em',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor:
          theme.palette.mode === 'dark'
            ? theme.palette.grey[500]
            : theme.palette.grey[400],
        padding: '3px 5px',
        verticalAlign: 'top',
        boxSizing: 'border-box',
        position: 'relative',

        '& > *': {
          marginBottom: 0,
        },
      },

      '& th': {
        fontWeight: 500,
        textAlign: 'left',
        backgroundColor: theme.palette.action.selected,
      },
      '& p': {
        margin: 0,
      },
    },

    // When in editing mode, the <table> element is wrapped in a div with a
    // `tableWrapper` class. When we have that arrangement, we change how
    // overflow works on the table to instead overflow with the wrapper and
    // revert back to display:table, as we'd typically expect/want.
    '& .tableWrapper': {
      overflowX: 'auto',

      '& table': {
        overflow: 'hidden',
        display: 'table',
      },
    },

    '& .selectedCell:after': {
      zIndex: 1,
      position: 'absolute',
      content: '""',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      background: 'rgba(200, 200, 255, 0.4)',
      pointerEvents: 'none',
    },

    // Only when the editor has `editable` set to `true` should the table column
    // resize tools should be revealed and be usable
    '&[contenteditable="true"]': {
      '& .column-resize-handle': {
        position: 'absolute',
        right: -2,
        top: -1,
        bottom: -2,
        width: 4,
        // This z-index proved necessary to ensure the handle sits above the
        // background of any cell (header and non-header)
        zIndex: 1,
        backgroundColor: theme.palette.primary.light,
        pointerEvents: 'none',
      },

      '&.resize-cursor': {
        cursor: 'col-resize',
      },
    },

    '&[contenteditable="false"]': {
      '& .column-resize-handle': {
        display: 'none',
      },

      '&.resize-cursor': {
        // To ensure that users cannot resize tables when the editor is supposed
        // to be read-only, we have to disable pointer events for the entire
        // editor whenever the resize-cursor class is added (i.e. when a user
        // hovers over a column border that would otherwise allow for dragging
        // and resizing when in editable mode). This is because the underlying
        // prosemirror-tables `columnResizing` plugin doesn't know/care about
        // `editable` state, and so adds the "resize-cursor" class and tries to
        // listen for events regardless.
        pointerEvents: 'none',
      },
    },
    '& .tiptap-ai-suggestion, & .tiptap-ai-suggestion > *': {
      backgroundColor: '#F2D7D5', // Alizarin light
      color: '#922B21', // Alizarin dark
    },
    '& .tiptap-ai-suggestion.tiptap-ai-suggestion--change-group, & .tiptap-ai-suggestion.tiptap-ai-suggestion--change-group > *:not(.tiptap-ai-suggestion-sub-change)':
      {
        backgroundColor: '#FDEDEC', // Alizarin lightest
      },
    '& .tiptap-ai-suggestion-sub-change': {
      backgroundColor: '#F2D7D5', // Alizarin light
    },
    '& .tiptap-ai-suggestion-diff, & .tiptap-ai-suggestion-diff > *': {
      backgroundColor: '#D5F5E3', // Emerald light
    },
    '& .tiptap-ai-suggestion-diff.tiptap-ai-suggestion-diff--change-group, & .tiptap-ai-suggestion-diff.tiptap-ai-suggestion-diff--change-group > *:not(.tiptap-ai-suggestion-diff-sub-change)':
      {
        backgroundColor: '#EAFAF1', // Emerald lightest
      },
    '& .tiptap-ai-suggestion-diff-sub-change': {
      backgroundColor: '#D5F5E3', // Emerald light
    },
    '& .tiptap-ai-suggestion-diff:has(tr)': {
      display: 'contents',
    },
    '& .tiptap-ai-suggestion-diff:has(tr) td, & .tiptap-ai-suggestion-diff:has(tr) th': {
      backgroundColor: '#D5F5E3', // Emerald light
    },
    // Style-guide suggestions: override default salmon background
    // and use category-specific wavy underlines instead
    '& .tiptap-ai-suggestion.style-guide-suggestion': {
      backgroundColor: 'transparent',
      color: 'inherit',
      cursor: 'pointer',
    },
    '& .tiptap-ai-suggestion.style-guide-suggestion > *': {
      backgroundColor: 'transparent',
      color: 'inherit',
    },
    // Shared underline style for all style-guide categories
    '& .tiptap-ai-suggestion[class*="style-category-"]': {
      textDecorationLine: 'underline',
      textDecorationStyle: 'solid',
      textDecorationThickness: '.175rem',
    },
    // Category-specific underline colors (Flat Design palette)
    '& .tiptap-ai-suggestion.style-category-abbreviations': {
      textDecorationColor: '#2980B9', // Belize Hole
    },
    '& .tiptap-ai-suggestion.style-category-capitalization': {
      textDecorationColor: '#8E44AD', // Wisteria
    },
    '& .tiptap-ai-suggestion.style-category-numbers': {
      textDecorationColor: '#E67E22', // Carrot
    },
    '& .tiptap-ai-suggestion.style-category-dates-times': {
      textDecorationColor: '#16A085', // Green Sea
    },
    '& .tiptap-ai-suggestion.style-category-punctuation': {
      textDecorationColor: '#C0392B', // Pomegranate
    },
    '& .tiptap-ai-suggestion.style-category-common-errors': {
      textDecorationColor: '#D35400', // Pumpkin
    },
    // Thread mark
    '& .tiptap-thread, & .tiptap-thread *': {
      transition: 'all 0.2s cubic-bezier(0.65,0.05,0.36,1)',
    },
    '& .tiptap-thread--block': {
      '&:first-of-type': {
        marginTop: 0,
      },
      '& > :not(p, h1, h2, h3, h4, h5, h6, ul, li)': {
        backgroundColor: theme.palette.primary.light,
        padding: '0.25rem',
        '&:hover, &.tiptap-thread--selected, &.tiptap-thread--hovered': {
          backgroundColor: theme.palette.primary.light,
        },
      },
      '& :is(p, h1, h2, h3, h4, h5, h6, ul, li)': {
        '&:not(:has(.tiptap-thread--inline))': {
          backgroundColor: theme.palette.primary.light,
          borderBottom: `2px dashed ${theme.palette.primary.main}`,
          '&:hover': {
            backgroundColor: theme.palette.primary.light,
          },
        },
      },
      '&.tiptap-thread--selected, &.tiptap-thread--hovered': {
        '& :is(p, h1, h2, h3, h4, h5, h6, ul, li)': {
          '&:not(:has(.tiptap-thread--inline))': {
            backgroundColor: theme.palette.primary.light,
          },
        },
      },
    },
    '& .tiptap-thread--inline': {
      '&:not(.tiptap-thread--resolved)': {
        borderBottom: `2px dashed ${theme.palette.primary.main}`,
        '&:hover, &.tiptap-thread--selected, &.tiptap-thread--hovered': {
          backgroundColor: theme.palette.primary.light,
        },
      },
    },
    '.search-result': {
      backgroundColor: theme.blox.colors.yellow,
    },
    '.search-result-current': {
      backgroundColor: theme.blox.colors.green,
    },
  },
});

export const removableInputStyle = (theme: Theme): SystemStyleObject => ({
  position: 'relative',
  '& .MuiButtonBase-root': {
    position: 'absolute',
    top: '50%',
    left: 135,
    transform: 'translateY(-50%)',
    color: theme.palette.grey[600],
  },
});

export const removeButtonStyle = (theme: Theme): SystemStyleObject => ({
  minHeight: 'auto',
  boxShadow: 'none',
  backgroundColor: 'transparent',
});
